import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Stack, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, TextField, MenuItem, Chip, Tooltip,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import {
  IEmailTemplate, IEmailTemplateMeta, EmailTemplateStatusFilter,
  CommunicationTemplateType,
  fetchEmailTemplateMeta, fetchEmailTemplates,
  deleteEmailTemplate, duplicateEmailTemplate,
  setEmailTemplateActive, setEmailTemplateDefault,
} from "../../../Api/emailTemplatesApi";
import { Loader } from "../../../Components/Loader";
import TemplateEditor from "./TemplateEditor";
import TemplatePreviewDialog from "./TemplatePreviewDialog";

/* ── Local UI palette ─ matches the rest of the Settings module (UserRoles
   uses a similar muted card + champagne accent vocabulary). Kept inline so
   this page is self-contained and theme-tweakable in one place. */
const NAVY  = "#0F172A";
const HAIRLINE = "rgba(10,14,26,0.06)";
const STATUS_COLORS = {
  active:   { bg: "rgba(22,163,74,0.10)", color: "#16a34a", border: "rgba(22,163,74,0.25)" },
  inactive: { bg: "rgba(100,116,139,0.10)", color: "#64748b", border: "rgba(100,116,139,0.25)" },
};

const EmailTemplatesPage: React.FC = () => {
  const [meta, setMeta] = useState<IEmailTemplateMeta | null>(null);
  const [rows, setRows] = useState<IEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch]         = useState("");
  const [templateType, setTemplateType] = useState<CommunicationTemplateType | "all">("all");
  const [category, setCategory]     = useState<string>("all");
  const [status, setStatus]         = useState<EmailTemplateStatusFilter>("all");

  const [editorOpen, setEditorOpen]   = useState(false);
  const [editing, setEditing]         = useState<IEmailTemplate | null>(null);
  const [previewing, setPreviewing]   = useState<IEmailTemplate | null>(null);
  const [confirmDel, setConfirmDel]   = useState<IEmailTemplate | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" }>({
    open: false, msg: "", sev: "success",
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchEmailTemplates({
        search: search || undefined,
        template_type: templateType === "all" ? undefined : templateType,
        category: category === "all" ? undefined : category,
        status,
      });
      setRows(list);
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message || "Failed to load templates.", sev: "error" });
    }
    setLoading(false);
  }, [search, templateType, category, status]);

  // Initial meta + first list pull.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchEmailTemplateMeta();
        if (!cancelled) setMeta(m);
      } catch (e: any) {
        if (!cancelled) setSnack({ open: true, msg: "Failed to load template metadata.", sev: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounce the search input — 300ms is enough to filter at typing
  // pace without flooding the API while a rep edits the term.
  useEffect(() => {
    const t = setTimeout(reload, 300);
    return () => clearTimeout(t);
  }, [reload]);

  /* ── Row actions ─────────────────────────────────────────────── */

  const onCreate = () => { setEditing(null); setEditorOpen(true); };
  const onEdit   = (row: IEmailTemplate) => { setEditing(row); setEditorOpen(true); };

  const onDuplicate = async (row: IEmailTemplate) => {
    try {
      await duplicateEmailTemplate(row.id);
      setSnack({ open: true, msg: `Duplicated "${row.name}".`, sev: "success" });
      reload();
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message || "Duplicate failed.", sev: "error" });
    }
  };

  const onToggleActive = async (row: IEmailTemplate) => {
    try {
      await setEmailTemplateActive(row.id, !row.is_active);
      setSnack({
        open: true,
        msg: row.is_active ? `Deactivated "${row.name}".` : `Activated "${row.name}".`,
        sev: "success",
      });
      reload();
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message || "Status change failed.", sev: "error" });
    }
  };

  const onSetDefault = async (row: IEmailTemplate) => {
    if (row.is_default) return;
    try {
      await setEmailTemplateDefault(row.id);
      setSnack({ open: true, msg: `"${row.name}" is now the default for its category.`, sev: "success" });
      reload();
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message || "Failed to set default.", sev: "error" });
    }
  };

  const onConfirmDelete = async () => {
    if (!confirmDel) return;
    try {
      await deleteEmailTemplate(confirmDel.id);
      setSnack({ open: true, msg: `Deleted "${confirmDel.name}".`, sev: "success" });
      setConfirmDel(null);
      reload();
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message || "Delete failed.", sev: "error" });
    }
  };

  // Category dropdown re-options when the type filter changes. Falls
  // back to email categories when "all types" is selected, so the
  // dropdown stays useful before the rep narrows by type.
  const categoryOptions = useMemo(() => {
    if (!meta) return [];
    if (templateType !== "all" && meta.categories_by_type?.[templateType]) {
      return meta.categories_by_type[templateType];
    }
    return meta.categories ?? [];
  }, [meta, templateType]);

  const typeOptions = useMemo(() => meta?.types ?? [], [meta]);

  // Reset the category filter whenever the type filter changes —
  // otherwise a category that doesn't exist on the new type would
  // silently filter the list to zero rows.
  useEffect(() => { setCategory("all"); }, [templateType]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── Header strip ───────────────────────────────────────── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" gap={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: NAVY, letterSpacing: "-0.01em" }}>
            Templates
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.2 }}>
            Reusable, variable-aware templates across email, call logs, internal notes, meetings, follow-ups, and SMS.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
          sx={{
            textTransform: "none", fontWeight: 700,
            bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" },
          }}
        >
          New Template
        </Button>
      </Stack>

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ p: 1.5, mb: 2, border: `1px solid ${HAIRLINE}` }}>
        <Stack direction={{ xs: "column", md: "row" }} gap={1.2} alignItems={{ md: "center" }}>
          <TextField
            size="small"
            placeholder="Search by name, slug, or subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small" select label="Type"
            value={templateType}
            onChange={(e) => setTemplateType(e.target.value as CommunicationTemplateType | "all")}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="all">All Types</MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" select label="Category"
            value={category} onChange={(e) => setCategory(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categoryOptions.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" select label="Status"
            value={status} onChange={(e) => setStatus(e.target.value as EmailTemplateStatusFilter)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
      </Card>

      {/* ── List ──────────────────────────────────────────────── */}
      {loading && !rows.length ? (
        <Loader />
      ) : (
        <TableContainer component={Card} variant="outlined" sx={{ border: `1px solid ${HAIRLINE}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafbfc" }}>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Template</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Default</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: NAVY }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 5, textAlign: "center", color: "#94a3b8" }}>
                      <Typography sx={{ fontSize: 13.5, fontStyle: "italic" }}>
                        No templates yet. Click <strong>New Template</strong> to create your first one.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const statusCfg = row.is_active ? STATUS_COLORS.active : STATUS_COLORS.inactive;
                  return (
                    <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: "#FDFAF4" } }}>
                      {/* Template — name + slug + 1-line description */}
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }} noWrap>
                          {row.name}
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={0.6} mt={0.2}>
                          <Typography sx={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 10.5, color: "#94a3b8",
                          }} noWrap>
                            {row.slug}
                          </Typography>
                          {row.description && (
                            <Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>
                              · {row.description}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {row.template_type_label ? (
                          <Chip
                            size="small"
                            label={row.template_type_label}
                            sx={{
                              fontSize: 10.5, fontWeight: 700,
                              bgcolor: row.template_type === "email" ? "#eff6ff" : "#fef3c7",
                              color:   row.template_type === "email" ? "#1e40af" : "#92400e",
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.category_label ? (
                          <Chip
                            size="small"
                            label={row.category_label}
                            sx={{
                              fontSize: 10.5, fontWeight: 700,
                              bgcolor: "#f1f5f9", color: "#475569",
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>—</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box sx={{
                          display: "inline-flex", alignItems: "center", gap: 0.4,
                          px: 0.9, py: 0.25, borderRadius: "999px",
                          bgcolor: statusCfg.bg, color: statusCfg.color,
                          border: `1px solid ${statusCfg.border}`,
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: statusCfg.color }} />
                          {row.is_active ? "Active" : "Inactive"}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={row.is_default ? "Default for this category" : "Set as default"} arrow>
                          <IconButton size="small" onClick={() => onSetDefault(row)} disabled={row.is_default}>
                            {row.is_default
                              ? <StarIcon sx={{ fontSize: 18, color: "#c9a961" }} />
                              : <StarBorderIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                          {row.updated_at ? new Date(row.updated_at).toLocaleString("en-US", {
                            month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
                          }) : "—"}
                        </Typography>
                        {row.updater?.name && (
                          <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                            by {row.updater.name}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Preview" arrow>
                          <IconButton size="small" onClick={() => setPreviewing(row)}>
                            <VisibilityIcon sx={{ fontSize: 16, color: "#64748b" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit" arrow>
                          <IconButton size="small" onClick={() => onEdit(row)}>
                            <EditIcon sx={{ fontSize: 16, color: "#64748b" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate" arrow>
                          <IconButton size="small" onClick={() => onDuplicate(row)}>
                            <ContentCopyIcon sx={{ fontSize: 15, color: "#64748b" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.is_active ? "Deactivate" : "Activate"} arrow>
                          <IconButton size="small" onClick={() => onToggleActive(row)}>
                            <PowerSettingsNewIcon sx={{ fontSize: 16, color: row.is_active ? "#16a34a" : "#94a3b8" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton size="small" onClick={() => setConfirmDel(row)}>
                            <DeleteIcon sx={{ fontSize: 16, color: "#dc2626" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Editor dialog ─────────────────────────────────────── */}
      {editorOpen && (
        <TemplateEditor
          open={editorOpen}
          template={editing}
          meta={meta}
          onClose={() => setEditorOpen(false)}
          onSaved={(t, msg) => {
            setEditorOpen(false);
            setSnack({ open: true, msg, sev: "success" });
            reload();
          }}
        />
      )}

      {/* ── Preview dialog ────────────────────────────────────── */}
      {previewing && (
        <TemplatePreviewDialog
          template={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}

      {/* ── Confirm delete ────────────────────────────────────── */}
      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete template?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            "{confirmDel?.name}" will be permanently removed. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDel(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={onConfirmDelete} variant="contained" color="error" sx={{ textTransform: "none", fontWeight: 700 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.sev}
          variant="filled"
          sx={{ fontWeight: 700 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailTemplatesPage;
